import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';
import restFactory from 'v2/utils/restFactory';
import { BLACK_LIST_REGEX } from 'v2/utils/formValidator';
import { Contact, DjangoListResponse } from 'v2/models';
import { QueryOptions } from 'v2/queries/types';

interface contactFilters {
  email?: string;
  data_vendor?: number;
  data_product?: number;
}

export default function useGetContacts(
  { email, data_vendor, data_product }: contactFilters,
  queryOptions: QueryOptions<DjangoListResponse<Contact>> = {},
  debounced = 500
) {
  const query = useQuery(
    ['useGetContacts', email, data_vendor, data_product],
    async () => {
      return await restFactory.get<DjangoListResponse<Contact>>(
        `/dj/api/v1/contact/`,
        { email, data_vendor, data_product }
      );
    },
    {
      refetchOnWindowFocus: false,
      enabled: false,
      ...queryOptions,
    }
  );

  const debouncedSearch = React.useRef(
    debounce(async () => {
      query.refetch();
    }, debounced)
  ).current;

  useEffect(() => {
    if (
      (!email || !email.match(BLACK_LIST_REGEX.email)) &&
      data_vendor === undefined &&
      data_product === undefined
    ) {
      debouncedSearch.cancel();
      query.remove();
    } else {
      debouncedSearch();
    }
  }, [email, data_vendor, data_product]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return query;
}
